import { X } from "lucide-react";
import { FormCheckbox, FormInput, FormSelect } from "@/components/forms";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Form } from "@/components/ui/form";

export function EditHeaderActionSheet({
  dataTypes = [],
  open,
  setOpen,
  onSubmit,
  setSelectedAttribute,
  form,
}: {
  dataTypes: any[];
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (data: any) => void;
  setSelectedAttribute: (attribute: any) => void;
  form: any;
}) {
  const handleClose = () => {
    setOpen(false);
    setSelectedAttribute(null);
    form.reset();
  };

  const handleSubmit = (data: any) => {
    onSubmit(data);
  };

  return (
    <Sheet open={open} modal={false}>
      <SheetContent
        side="right"
        className="aero:sm:max-w-[600px] aero:p-0 aero:gap-0 aero:flex aero:flex-col"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Custom close button - hide default */}
        <style>{`
          [data-slot="sheet-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>

        {/* Header */}
        <SheetHeader className="aero:p-6 aero:pb-4 aero:gap-3 aero:border-b aero:border-neutral-200">
          <div className="aero:flex aero:items-center aero:justify-between aero:h-12 aero:gap-3">
            <SheetTitle className="aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
              Edit Attribute
            </SheetTitle>
            <SheetClose asChild>
              <button
                onClick={handleClose}
                className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100"
              >
                <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6 aero:py-5">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="aero:flex aero:flex-col aero:gap-5"
            >
              {/* Section: Attribute Information */}
              <div className="aero:flex aero:flex-col aero:gap-5">
                <div className="aero:flex aero:items-center aero:gap-2">
                  <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                  <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                    Attribute Information
                  </h3>
                </div>

                <div className="aero:flex aero:flex-col aero:gap-4">
                  <FormInput
                    name="label"
                    label="Attribute Label"
                    placeholder="Enter attribute label"
                    control={form.control}
                    required
                  />
                  <FormSelect
                    name="dataType"
                    label="Data Type"
                    placeholder="Select data type"
                    triggerClassName="aero:w-full"
                    options={
                      dataTypes?.map((i: any) => ({
                        label: i.displayName,
                        value: i.id,
                      })) || []
                    }
                    control={form.control}
                    required
                  />
                </div>
              </div>

              {/* Section: Options */}
              <div className="aero:flex aero:flex-col aero:gap-4">
                <div className="aero:flex aero:items-center aero:gap-2">
                  <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                  <h3 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                    Options
                  </h3>
                </div>

                <div className="aero:flex aero:items-center aero:gap-4">
                  <FormCheckbox
                    name="isUnique"
                    label="Unique"
                    control={form.control}
                  />
                  <FormCheckbox
                    name="isRequired"
                    label="Required"
                    control={form.control}
                  />
                  <FormCheckbox
                    name="isActive"
                    label="Active"
                    control={form.control}
                  />
                </div>
              </div>

              {/* Footer */}
              <SheetFooter className="aero:mt-auto aero:pt-6 aero:border-t aero:border-neutral-200">
                <div className="aero:flex aero:items-center aero:gap-3 aero:w-full">
                  <Button
                    type="button"
                    intent="neutral"
                    design="outline"
                    className="aero:flex-1 aero:h-10"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    intent="primary"
                    className="aero:flex-1 aero:h-10"
                  >
                    Update Column
                  </Button>
                </div>
              </SheetFooter>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
